import { MiscUtil } from './utility/MiscUtil';
import { NormalizedEvent } from './event/NormalizedEvent';
import { PseudoEventName } from '../gen-src/preslayer/Enums';
import { TypeUtil } from './utility/TypeUtil';

// ====================================================================================================================
//
// ****** Comprehensive Documentation for Event Handling System *******
//
// Create a new EventHandler to give a dom node gestures. The client should keep the handler and call its
// destroy() method when done with it. You can construct with only the callbacks you need -- you don't need to specify
// all of them. To preventDefault, you must call e.preventDefault() in the object returned to you in the callback.
// Propagation is stopped for you. Always. Always and forever.
//
// The event object passed to the callback should be treated like a real event object, though it isn't actually one.
// If there is any event property you need that the pseudo-event object does not have, please add it yourself
// in Gesture.js::fire. All event objects have {timestamp:number, gesture: string, callback: string}
// properties. Additional properties vary based on gesture type - see below.
//
// Copy and paste usage of EventHandler:
// var eventHandler = new tableau.base.EventHandler(domElement, {
//
//        // DISCRETE: e = touch event object augmented with pageX, pageY
//        'firstTouch':           function (e) {},
//        'lastTouch':            function (e) {},
//        'cancelPotentialDoubleTap:    function (e) {}, // fired when doubleTap is not fired after the first Tap.
//        'tap':                  function (e) {},
//        'doubleTap':            function (e) {},
//        'potentialPress':       function (e) {},
//        'cancelPotentialPress': function (e) {}, // fired when potentialPress is fired but press is not fired
//        'press':                function (e) {},
//        'tapAndHalf':           function (e) {},  //NOT YET IMPLEMENTED Probably use same callback as press
//
//        // SINGLE FINGER CONTINUOUS: e with {pageX, pageY, deltaX, deltaY (change in position from beginning
//        // of gesture), deltaXSinceLast, deltaYSinceLast (change since last callback)}
//        'dragStart':            function (e) {},
//        'dragMove':             function (e) {},
//        'dragEnd':              function (e) {},
//        'pressDragStart':       function (e) {},
//        'pressDragMove':        function (e) {},
//        'pressDragEnd':         function (e) {},
//
//        // TWO FINGER CONTINUOUS: e with {pageX, pageY, scale, deltaX, deltaY (change in center point between the
//        // two fingers since beginning of gesture), deltaXSinceLast, deltaYSinceLast (change in center point
//        // between the two fingers since last callback)},
//        'pinchStart':           function (e) {},
//        'pinchMove':            function (e) {},
//        'pinchEnd':             function (e) {},
//        'doublePressDragStart': function (e) {},
//        'doublePressDragMove':  function (e) {},
//        'doublePressDragEnd':   function (e) {},
//
//        // HYBRID MUTANT SAVAGES: Gets touch object of tap finger.
//        'HoldTapStart':         function (e) {},  //NOT YET IMPLEMENTED
//        'HoldTapTap':           function (e) {},  //NOT YET IMPLEMENTED
//        'HoldTapEnd':           function (e) {},  //NOT YET IMPLEMENTED
//
//        // CONFIGURATION OPTIONS
//        'debug':    true, // defaults false. Will leave feedback in a box on bottom right whenever event is fired
// });
// eventHandler.destroy(); //call before destroying your domElement.
//
//
// *********** Global Events **************
//
// There are global events fired just prior to Discrete's handling of certain events. This gives you a chance to do something
// prior to the event being handled, and also allows you to cancel the event. This allows things like tooltips, dialogs,
// type-in pill, etc. watch for and react to events external to themselves.
//
// Each of the events allows you to setup a handler where params are the NormalizedEvent and a callback.  If you invoke the
// the callback, the default Discrete behavior for the event will be canceled.  If you pass true to the callback, preventDefault
// will also be called on the event object.
//
// To handle to these global events:
//
//  public void Init() {
//      GlobalUIEvents.Press += this.OnGlobalPress;
//  }
//  public void Destroy() {
//      GlobalUIEvents.Press -= this.OnGlobalPress;
//  }
//  private void OnGlobalPress(NormalizedEvent e, Action<bool> cancelCallback)
//  {
//      if (!DomUtil.IsAncestorOf(this.domNode, e.target)) {
//          this.HideOrDoOtherStuff();
//          cancelCallback(true);
//      }
//  }
//
// <see cref="GlobalUIEvents"/>

// ====================================================================================================================

// TODO:
// - Potential bug: If both fingers hit perfectly simulataneously on two different dom elements,
//                  no firstTouch is fired. This shouldn't be a problem.

// - Improve: Dynamically add move and end event listeners for touch.
// - Improve: Pinch simulation in desktop. Shift click simulates center point? Implement in NormalizedEvent?
// - Right now we run all gestures no matter what the client cares about, but the event only gets fired if a
//   callback exists.

/** Config for the <see cref="TableauEventHandler"/>*/
/// <remarks>Ideally we'd derive from <see cref="Record"/> but currently we cannot since that will initialize
/// all fields to null.  This causes issues since we use <see cref="jQuery.Extend(object,object[])"/> to merge
/// instances of configs.  And even though the jQuery docs say otherwise null properties are copied over,
/// which causes issues for us.</remarks>
class EventHandleSpec {
  // DISCRETE: e = touch event object augmented with pageX, pageY
  public firstTouch: (_: NormalizedEvent) => void = undefined;
  // this is just the touchMove event, and not mouse move or (mousedown + move) on web
  public touchMove: (_: NormalizedEvent) => void = undefined;
  public lastTouch: (_: NormalizedEvent) => void = undefined;
  // fired when doubleTap is not fired after the first Tap.
  public cancelPotentialDoubleTap: (_: NormalizedEvent) => void = undefined;
  public tap: (_: NormalizedEvent) => void = undefined;
  public doubleTap: (_: NormalizedEvent) => void = undefined;
  public potentialPress: (_: NormalizedEvent) => void = undefined;
  // fired when potentialPress is fired but press is not fired
  public cancelPotentialPress: (_: NormalizedEvent) => void = undefined;
  public press: (_: NormalizedEvent) => void = undefined;
  public pressEnd: (_: NormalizedEvent) => void = undefined;
  public rightClick: (_: NormalizedEvent) => void = undefined;
  // NOT YET IMPLEMENTED Probably use same callback as press
  public tapAndHalf: (_: NormalizedEvent) => void = undefined;

  // HOVER
  public hover: (_: NormalizedEvent) => void = undefined;
  private hoverDelay: number;

  // SINGLE FINGER CONTINUOUS: e with {pageX, pageY, deltaX, deltaY (change in position from beginning
  // of gesture), deltaXSinceLast, deltaYSinceLast (change since last callback)}
  public dragStart: (_: NormalizedEvent) => void = undefined;
  public dragMove: (_: NormalizedEvent) => void = undefined;
  public dragEnd: (_: NormalizedEvent) => void = undefined;
  public pressDragStart: (_: NormalizedEvent) => void = undefined;
  public pressDragMove: (_: NormalizedEvent) => void = undefined;
  public pressDragEnd: (_: NormalizedEvent) => void = undefined;

  // TWO FINGER CONTINUOUS: e with {pageX, pageY, scale, deltaX, deltaY (change in center point between the
  // two fingers since beginning of gesture), deltaXSinceLast, deltaYSinceLast (change in center point
  // between the two fingers since last callback)},
  public pinchStart: (_: NormalizedEvent) => void = undefined;
  public pinchMove: (_: NormalizedEvent) => void = undefined;
  public pinchEnd: (_: NormalizedEvent) => void = undefined;
  public doublePressDragStart: (_: NormalizedEvent) => void = undefined;
  public doublePressDragMove: (_: NormalizedEvent) => void = undefined;
  public doublePressDragEnd: (_: NormalizedEvent) => void = undefined;

  // HYBRID MUTANT SAVAGES: Gets touch object of tap finger.
  public holdTapStart: (_: NormalizedEvent) => void = undefined; // NOT YET IMPLEMENTED
  public holdTapTap: (_: NormalizedEvent) => void = undefined; // NOT YET IMPLEMENTED
  public holdTapEnd: (_: NormalizedEvent) => void = undefined; // NOT YET IMPLEMENTED
  private domElem: HTMLElement = undefined;

  public get DomElement(): HTMLElement {
    return this.domElem;
  }

  public set DomElement(dom: HTMLElement) {
    this.domElem = dom;
  }

  public get HoverDelay(): number {
    return this.hoverDelay;
  }

  public set HoverDelay(delay: number) {
    this.hoverDelay = delay;
  }

  // Extension methods
  public IsHandling(evt: PseudoEventName): boolean {
    let dict = this as Object;
    let result = TypeUtil.GetField(dict, evt);
    return !MiscUtil.IsNullOrUndefined(result);
  }

  public IsUsingHover(): boolean {
    return this.IsHandling(PseudoEventName.Hover) ||
      this.IsHandling(PseudoEventName.Moving);
  }

  /**
  * Returns true if any pressDrag related callbacks are set
  */
  public UsingPressDrag(): boolean {
    return this.IsHandling(PseudoEventName.PressDragStart) ||
      this.IsHandling(PseudoEventName.PressDragMove) ||
      this.IsHandling(PseudoEventName.PressDragEnd) ||
      this.IsHandling(PseudoEventName.DoublePressDragStart) ||
      this.IsHandling(PseudoEventName.DoublePressDragMove) ||
      this.IsHandling(PseudoEventName.DoublePressDragEnd);
  }

  public SetHandler(evt: PseudoEventName, fn: Function): void {
    TypeUtil.SetField(this, evt, fn);
  }

  public RemoveHandler(evt: PseudoEventName): void {
    TypeUtil.SetField(this, evt, null);
  }

}

export { EventHandleSpec };
